import { currencyLabel } from '../currency/status.js';
import { TAGS } from '../tags/registry.js';
export const PROSPECT_COLUMNS = [
    {
        key: 'jurisdiction_name',
        label: 'Jurisdiction',
        definition: 'The city, town or county that issues the permits.',
    },
    {
        key: 'type',
        label: 'Type',
        definition: 'City (a city, town or other incorporated place) or County (permits the county issues, usually for unincorporated areas).',
    },
    { key: 'state', label: 'State', definition: 'Two-letter state code.' },
    { key: 'county_name', label: 'County', definition: 'The county the jurisdiction is in.' },
    {
        key: 'cbsa_name',
        label: 'Metro (CBSA)',
        definition: 'The Census core-based statistical area (metro or micro area) the jurisdiction belongs to.',
    },
    {
        key: 'population',
        label: 'Population',
        numeric: true,
        definition: 'Residents the jurisdiction issues permits for (Census 2023 estimates). For a county, the unincorporated area it permits.',
    },
    {
        key: 'permit_count',
        label: 'Our permits',
        numeric: true,
        definition: 'Permits we hold for the jurisdiction, all years.',
    },
    {
        key: 'coverage_start',
        label: 'Earliest permit',
        definition: 'Date of the earliest permit we hold. Some years after it may be thin or empty, so this is not a promise of continuous history.',
    },
    { key: 'coverage_end', label: 'Coverage end', definition: 'Date of the newest permit we hold.' },
    {
        key: 'permits_last_12mo',
        label: 'Permits (last 12mo)',
        numeric: true,
        definition: 'Permits dated in the 12 months up to our newest permit for the jurisdiction.',
    },
    {
        key: 'permits_per_10k',
        label: 'Permits / 10K pop',
        numeric: true,
        definition: 'Permits in the last 12 months per 10,000 residents. A rough gauge of how complete the feed is.',
    },
    {
        key: 'currency_status',
        label: 'Currency',
        definition: 'Current: recent permits at a healthy volume. Thin recent activity: recent permits, but last-year volume has collapsed. Stale: no permit in the last 12 months. Undated: the permits carry no dates.',
    },
    {
        key: 'trade_counts',
        label: 'Trades',
        definition: "The trades most often on this jurisdiction's permits (electrical, plumbing, mechanical, …).",
    },
    {
        key: 'tag_counts',
        label: 'Tags',
        definition: "The tags most often on this jurisdiction's permits. See the data dictionary for each tag.",
    },
    {
        key: 'bps_volume',
        label: 'BPS volume',
        numeric: true,
        definition: 'Census Building Permits Survey: new housing units this jurisdiction reported authorizing in the latest year. A yardstick for how much residential building it permits, not a count of our permits.',
    },
    {
        key: 'pct_geocodeable',
        label: '% with location',
        numeric: true,
        definition: 'Share of our permits for the jurisdiction with an address we can place on a map.',
    },
];
export function toProspectJurisdiction(r) {
    const fips5 = r.county_fips ?? null;
    return {
        id: r.id,
        type: r.kind === 'county' ? 'County' : 'City',
        jurisdiction_name: r.jurisdiction_name,
        state: r.state,
        state_fips: (r.place_geoid ?? fips5)?.slice(0, 2) ?? null,
        county_fips: fips5,
        county_name: r.county_name,
        cbsa_name: r.cbsa_name,
        population: r.population,
        permit_count: r.permit_count,
        coverage_start: r.coverage_start,
        coverage_end: r.coverage_end,
        permits_last_12mo: r.permits_last_12mo,
        permits_per_10k: r.permits_per_10k,
        currency_status: r.currency_status ?? null,
        trade_counts: r.trade_counts ?? {},
        tag_counts: r.tag_counts ?? {},
        bps_volume: r.bps_volume,
        pct_geocodeable: r.pct_geocodeable,
        tag_counts_10y: r.tag_counts_10y ?? {},
        permit_count_10y: r.permit_count_10y ?? null,
    };
}
/** The n most frequent keys of a value → count map. */
export function topKeys(counts, n = 3) {
    return Object.entries(counts)
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
        .slice(0, n)
        .map(([k]) => k);
}
/** Display / CSV text for one cell. */
export function cellText(r, key) {
    const v = r[key];
    if (v == null)
        return '';
    switch (key) {
        case 'trade_counts':
        case 'tag_counts':
            return topKeys(v).join('; ');
        case 'pct_geocodeable':
            return `${(v * 100).toFixed(1)}%`;
        case 'permits_per_10k':
            return v.toFixed(1);
        case 'currency_status':
            return currencyLabel(v);
        default:
            return String(v);
    }
}
const sortValue = (r, key) => {
    const v = r[key];
    if (key === 'trade_counts' || key === 'tag_counts')
        return cellText(r, key) || null;
    return v ?? null;
};
export function filterSortJurisdictions(rows, query, sortKey, dir) {
    const q = query.trim().toLowerCase();
    const matched = q
        ? rows.filter((r) => [r.jurisdiction_name, r.state, r.cbsa_name].some((v) => v?.toLowerCase().includes(q)))
        : [...rows];
    const sign = dir === 'asc' ? 1 : -1;
    return matched.sort((a, b) => {
        const av = sortValue(a, sortKey);
        const bv = sortValue(b, sortKey);
        // Blanks always last, whichever direction.
        if (av == null || bv == null)
            return av == null ? (bv == null ? 0 : 1) : -1;
        const c = typeof av === 'number' && typeof bv === 'number'
            ? av - bv
            : String(av).localeCompare(String(bv));
        return c * sign || a.jurisdiction_name.localeCompare(b.jurisdiction_name);
    });
}
const csvCell = (v) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
/**
 * The prospect CSV: kept columns under their plain names, state + county FIPS
 * as zero-padded text, then one column per tag (dictionary family order): the
 * share of the jurisdiction's last-10-year permits carrying it.
 */
export function jurisdictionsCsv(rows) {
    const base = PROSPECT_COLUMNS.map((c) => c.label);
    // FIPS sit right after State so a join key is easy to find.
    const header = [
        ...base.slice(0, 3),
        'State FIPS',
        'County FIPS',
        ...base.slice(3),
        ...TAGS.map((t) => `${t.tag} share (10 yr)`),
    ];
    const lines = rows.map((r) => {
        const cells = PROSPECT_COLUMNS.map((c) => cellText(r, c.key));
        const total = r.permit_count_10y;
        const shares = TAGS.map((t) => total ? ((r.tag_counts_10y[t.tag] ?? 0) / total).toFixed(4) : '');
        return [
            ...cells.slice(0, 3),
            r.state_fips ?? '',
            r.county_fips ?? '',
            ...cells.slice(3),
            ...shares,
        ]
            .map(csvCell)
            .join(',');
    });
    return `${[header.join(','), ...lines].join('\n')}\n`;
}
//# sourceMappingURL=jurisdictionTable.js.map